import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * <cart-upsell-component>
 * Tag-based dynamic upsell tiles inside the cart page and cart drawer.
 *
 * Listens for cart updates and re-renders its parent section so the
 * recommendations stay in sync with current cart contents.
 */
class CartUpsellComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleClick = (event) => {
    const button = event.target.closest('.vt-upsell__add');
    if (!button || button.disabled) return;
    event.preventDefault();
    this.#addToCart(button);
  };

  #addToCart = async (button) => {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.dataset.loading = 'true';
    button.disabled = true;

    try {
      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: this.#getSectionsToRefresh(),
      });

      const response = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      const data = await response.json();

      // Notify the rest of the theme — cart drawer, header bubble, totals, etc.
      document.dispatchEvent(new CartAddEvent(data, this.#sectionId, { source: 'cart-upsell' }));
      document.dispatchEvent(new CartUpdateEvent(data, this.#sectionId, { source: 'cart-upsell' }));

      // Visual success state
      button.dataset.loading = 'false';
      button.dataset.success = 'true';
      const label = button.querySelector('.vt-upsell__add-label');
      if (label) label.textContent = '✓';

      setTimeout(() => {
        button.dataset.success = 'false';
        if (label) label.textContent = 'Add';
        button.disabled = false;
      }, 1200);
    } catch (err) {
      console.error('[cart-upsell] add failed', err);
      button.dataset.loading = 'false';
      button.disabled = false;
      const label = button.querySelector('.vt-upsell__add-label');
      if (label) {
        label.textContent = 'Error';
        setTimeout(() => { label.textContent = 'Add'; }, 1500);
      }
    }
  };

  #handleCartUpdate = () => {
    // When cart contents change (item added/removed elsewhere), re-render
    // the parent section so our recommendations recompute against the new cart.
    if (!this.#sectionId) return;
    sectionRenderer.renderSection(this.#sectionId, { cache: false });
  };

  get #sectionId() {
    const sectionEl = this.closest('[data-section-id]') || this.closest('.shopify-section');
    if (!sectionEl) return null;
    if (sectionEl.dataset.sectionId) return sectionEl.dataset.sectionId;
    if (sectionEl.id && sectionEl.id.startsWith('shopify-section-')) {
      return sectionEl.id.replace('shopify-section-', '');
    }
    return null;
  }

  #getSectionsToRefresh() {
    const sections = new Set();
    document
      .querySelectorAll('[data-section-id]')
      .forEach((el) => {
        const id = el.dataset.sectionId;
        if (id) sections.add(id);
      });
    document
      .querySelectorAll('[id^="shopify-section-"]')
      .forEach((el) => {
        const id = el.id.replace('shopify-section-', '');
        // Only include cart-related sections to keep the response small
        if (/cart/i.test(id)) sections.add(id);
      });
    return Array.from(sections);
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
